import { Directive, ElementRef, HostListener, Inject, Input, PLATFORM_ID } from '@angular/core';
import type { OnInit } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ElementTrackingService } from '@core-mkt/services/element-tracking/element-tracking.service';
import {
  type ElementPosition,
  type ElementValue,
  type InteractionSource,
  type InteractionType,
  type ElementState,
} from '@core-mkt/services/element-tracking/element-tracking.types';

/**
 * TrackElementDirective
 *
 * SSR-safe directive for declarative element interaction tracking.
 * Automatically binds to DOM events and tracks interactions via ElementTrackingService.
 *
 * Features:
 * - Declarative tracking via template attributes
 * - Auto-binds to configurable DOM events (click, focus, submit, etc.)
 * - SSR-safe (no event listeners during SSR)
 * - Auto-detects element position if not specified
 * - Sensible defaults based on element type
 *
 * @example
 * // Basic usage - tracks click by default for buttons
 * <button
 *   trackElement
 *   elementId="home_cta_signup_body"
 *   elementType="button"
 *   elementPosition="body">
 *   Sign Up
 * </button>
 *
 * @example
 * // Form input with focus tracking
 * <input
 *   trackElement
 *   elementId="home_emailform_email_footer"
 *   elementType="email_form"
 *   elementPosition="footer"
 *   [trackEvents]="['focus']"
 *   type="email">
 *
 * @example
 * // Track multiple events
 * <input
 *   trackElement
 *   elementId="home_emailform_email_body"
 *   elementType="email_form"
 *   elementPosition="body"
 *   [trackEvents]="['focus', 'blur']"
 *   type="email">
 */
@Directive({
  selector: '[trackElement]',
  standalone: true,
})
export class TrackElementDirective implements OnInit {
  private isBrowser: boolean;

  /**
   * Element ID in format: [page]_[component]_[descriptor]_[position]
   * Example: 'home_emailform_signup_footer'
   * REQUIRED - IDE will show error if missing
   */
  @Input({ required: true }) elementId!: string;

  /**
   * Type of UI element (e.g., 'email_form', 'button', 'link')
   * REQUIRED - IDE will show error if missing
   */
  @Input({ required: true }) elementType!: string;

  /**
   * Position within the page (header, footer, sidebar, body)
   * REQUIRED
   */
  @Input({ required: true }) elementPosition!: ElementPosition;

  /**
   * Which DOM events to track
   * Default: ['click'] for buttons/links, ['focus'] for inputs
   */
  @Input() trackEvents: string[] = [];

  /**
   * Interaction source (user or system)
   * Default: 'user'
   */
  @Input() interactionSource: InteractionSource = 'user';

  /**
   * Element value - data to capture with the interaction
   */
  @Input() elementValue?: ElementValue;

  /**
   * Element state for the interaction
   */
  @Input() elementState?: ElementState;

  /**
   * Destination URL (for links, navigation elements)
   */
  @Input() destinationUrl?: string;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private elementRef: ElementRef<HTMLElement>,
    private elementTrackingService: ElementTrackingService,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    // Validate required inputs
    if (!this.elementId) {
      // eslint-disable-next-line no-console
      console.warn('TrackElementDirective: Missing required input "elementId"');
      return;
    }

    if (!this.elementType) {
      // eslint-disable-next-line no-console
      console.warn('TrackElementDirective: Missing required input "elementType"');
      return;
    }

    if (!this.elementPosition) {
      // eslint-disable-next-line no-console
      console.warn('TrackElementDirective: Missing required input "elementPosition"');
      return;
    }

    // Set default track events based on element type if not specified
    if (this.trackEvents.length === 0) {
      this.trackEvents = this.getDefaultTrackEvents();
    }
  }

  /**
   * Get default track events based on element type
   */
  private getDefaultTrackEvents(): string[] {
    const element = this.elementRef.nativeElement;
    const tagName = element.tagName.toLowerCase();

    if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
      return ['focus'];
    }

    if (tagName === 'form') {
      return ['submit'];
    }

    // Default to click for buttons, links, etc.
    return ['click'];
  }

  /**
   * Handle click events
   */
  @HostListener('click')
  onClick(): void {
    if (this.isBrowser && this.trackEvents.includes('click')) {
      this.trackInteraction('click');
    }
  }

  /**
   * Handle focus events
   */
  @HostListener('focus')
  onFocus(): void {
    if (this.isBrowser && this.trackEvents.includes('focus')) {
      this.trackInteraction('focus');
    }
  }

  /**
   * Handle blur events
   */
  @HostListener('blur')
  onBlur(): void {
    if (this.isBrowser && this.trackEvents.includes('blur')) {
      this.trackInteraction('focus'); // Blur is tracked as focus interaction
    }
  }

  /**
   * Handle submit events (for forms)
   */
  @HostListener('submit')
  onSubmit(): void {
    if (this.isBrowser && this.trackEvents.includes('submit')) {
      this.trackInteraction('submit');
    }
  }

  /**
   * Handle change events (for selects, checkboxes, radios)
   */
  @HostListener('change', ['$event'])
  onChange(event: Event): void {
    if (this.isBrowser && this.trackEvents.includes('change')) {
      const target = event.target as HTMLInputElement;

      // Determine interaction type based on input type
      let interactionType: InteractionType = 'type';
      if (target.type === 'checkbox' || target.type === 'radio') {
        interactionType = 'check';
      }

      this.trackInteraction(interactionType);
    }
  }

  /**
   * Handle mouseenter events (for hover tracking)
   */
  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.isBrowser && this.trackEvents.includes('hover')) {
      this.trackInteraction('hover');
    }
  }

  /**
   * Track the interaction using ElementTrackingService
   */
  private trackInteraction(interactionType: InteractionType): void {
    if (!this.elementPosition) {
      return;
    }

    this.elementTrackingService.trackInteraction({
      elementId: this.elementId,
      elementType: this.elementType,
      elementPosition: this.elementPosition,
      interactionType,
      interactionSource: this.interactionSource,
      elementValue: this.elementValue,
      elementState: this.elementState,
      destinationUrl: this.destinationUrl,
    });
  }

  /**
   * Public method to manually trigger tracking
   * Useful for programmatic tracking or custom event handling
   *
   * @param interactionType - Type of interaction to track
   * @param success - Whether the interaction was successful
   */
  public track(interactionType: InteractionType, success = true): void {
    if (!this.isBrowser || !this.elementPosition) {
      return;
    }

    this.elementTrackingService.trackInteraction(
      {
        elementId: this.elementId,
        elementType: this.elementType,
        elementPosition: this.elementPosition,
        interactionType,
        interactionSource: this.interactionSource,
        elementValue: this.elementValue,
        elementState: this.elementState,
        destinationUrl: this.destinationUrl,
      },
      success,
    );
  }
}
